import React from 'react';
import { Upload, Loader } from 'lucide-react';
import MediaItem from './MediaItem';

/**
 * Componente reutilizable para manejar la sección de medios
 *
 * @param {Object} props
 * @param {Array} props.mediaFiles - Lista de archivos multimedia
 * @param {boolean} props.loading - Estado de carga
 * @param {Object} props.mediaErrors - Errores de carga
 * @param {Object} props.fileInputRef - Referencia al input de archivo
 * @param {boolean} props.dragActive - Estado de arrastre activo
 * @param {string} props.viewMode - Modo de visualización ('grid' o 'carousel')
 * @param {function} props.setViewMode - Función para cambiar el modo de visualización
 * @param {function} props.handleInputChange - Manejador de cambio de input
 * @param {function} props.handleDrag - Manejador de eventos de arrastre
 * @param {function} props.handleDrop - Manejador de eventos de soltar
 * @param {function} props.removeMedia - Función para eliminar medios
 * @param {function} props.handleMediaError - Función para manejar errores de carga
 * @param {function} props.setMediaErrors - Función para establecer errores de carga
 * @param {string} props.uploadText - Texto para el botón de carga (opcional)
 * @param {string} props.uploadDescription - Descripción para el botón de carga (opcional)
 * @param {string} props.title - Título de la sección (opcional)
 * @param {boolean} props.disabled - Estado de deshabilitado
 * @param {string} props.type - Tipo de sección ('logo' o 'generic')
 * @param {string} props.acceptedFiles - MIME types permitidos para el input (p.ej. 'image/*', 'video/*')
 * @param {boolean} props.compact - Modo compacto para UIs con espacio limitado
 */
const MediaSection = ({
  mediaFiles = [],
  loading,
  mediaErrors,
  fileInputRef,
  dragActive,
  handleInputChange,
  handleDrag,
  handleDrop,
  removeMedia,
  handleMediaError,
  setMediaErrors,
  title = "Medios",
  uploadText = "Sube archivos",
  uploadDescription,
  disabled = false,
  type = 'generic',
  acceptedFiles,
  showTitle = true,
  forceVideo = false,
  compact = false
}) => {
  const defaultAccept = type === 'logo' ? 'image/*' : 'image/*,video/*';
  const acceptAttr = acceptedFiles || defaultAccept;

  const showUploadButton = type === 'generic' || (type === 'logo' && mediaFiles.length === 0);

  const handleUploadClick = () => {
    if (!fileInputRef) {
      console.warn('fileInputRef is not definido');
      return;
    }
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const wrapperPadding = compact ? 'p-3' : 'p-5';
  const titleClasses = compact ? 'text-base' : 'text-lg';
  const titleMargin = compact ? 'mb-2' : 'mb-4';
  const gridGapClass = compact ? 'gap-3' : 'gap-4';
  const dropzonePadding = compact ? 'p-4' : 'p-6';
  const iconSize = compact ? 'w-6 h-6' : 'w-8 h-8';

  return (
    <div className={wrapperPadding}>
      {showTitle && (
        <div className={titleMargin}>
          <h3 className={`${titleClasses} font-medium text-gray-900`}>{title}</h3>
          {uploadDescription && (
            <p className={`text-sm text-gray-500 mt-1 ${compact ? 'leading-snug' : ''}`}>{uploadDescription}</p>
          )}
        </div>
      )}

      {mediaFiles.length > 0 && (
        <div className="mb-4">
          <div className={`grid ${gridGapClass} ${type === 'logo' ? 'grid-cols-1 max-w-xs mx-auto' : 'grid-cols-2 md:grid-cols-3'}`}>
            {mediaFiles.map((media, index) => (
              <MediaItem
                key={media.id}
                media={media}
                index={index}
                mediaErrors={mediaErrors}
                onDelete={disabled ? null : removeMedia}
                handleMediaError={handleMediaError}
                setMediaErrors={setMediaErrors}
                forceVideo={forceVideo}
              />
            ))}
          </div>
        </div>
      )}

      {showUploadButton && !disabled && (
        <div
          className={`relative border-2 border-dashed rounded-lg ${dropzonePadding} ${
            loading 
              ? 'cursor-not-allowed border-gray-200 bg-gray-50' 
              : `cursor-pointer ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`
          }`}
          onDragEnter={loading ? undefined : handleDrag}
          onDragLeave={loading ? undefined : handleDrag}
          onDragOver={loading ? undefined : handleDrag}
          onDrop={loading ? undefined : handleDrop}
          onClick={loading ? undefined : handleUploadClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleInputChange}
            accept={acceptAttr}
            multiple={type !== 'logo'}
            disabled={loading}
          />
          <div className="text-center">
            <div className="flex justify-center mb-2">
              {loading ? (
                <Loader className={`${iconSize} text-gray-400 animate-spin`} />
              ) : (
                <Upload className={`${iconSize} text-gray-400`} />
              )}
            </div>
            <p className={`text-sm font-medium ${loading ? 'text-gray-500' : 'text-gray-900'}`}>
              {loading ? 'Subiendo...' : uploadText}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {loading 
                ? 'Por favor espera mientras se sube el archivo'
                : type === 'logo' 
                  ? 'Solo se permite una imagen cuadrada' 
                  : 'Arrastra y suelta o haz clic para seleccionar archivos'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaSection;
